import i18n from './i18n';
// Must be a static import so the OAuth listener is registered before Amplify
// processes any OAuth callback in the URL (dynamic import would be too late).
// Safe in mock mode — no OAuth events fire when Amplify is not configured.
import 'aws-amplify/auth/enable-oauth-listener';
import { PnetAuthProvider, type PnetAuthConfig } from '@asi-lda/pnet-auth-web/react';
import { amplifyConfig } from './amplifyconfiguration';
import { isMockAuthEnabled } from '@/config/authMode';
import { authTokenKind } from '@/config/authToken';
import { authSessionMode, identityApiUrl, SESSION_CLIENT_KEY } from '@/config/authSession';
import { SessionRegistry } from '@/auth/sessionRegistry';
import { SessionProvider } from '@/auth/SessionProvider';
import { queryClient } from '@/query/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { I18nextProvider } from 'react-i18next';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { installStaleBuildReload } from '@/utils/staleBuildReload';

// A tab opened before a deploy reloads once instead of showing the error page.
installStaleBuildReload();
import { ToastContainer } from 'react-toastify';
import useZoomDetection from '@/utils/zoomDetection';
import { LicenseInfo } from '@mui/x-license';
import { SidebarNavigationProvider } from '@/contexts/sidebarNavigationContext';
import { LoadingProvider } from '@/contexts/LoadingContext';
import ConfirmationProvider from '@/provider/ConfirmationProvider';
import { StyledEngineProvider } from '@mui/system';
import Pages from './routes';
import { ThemeComponentProvider } from '@/components/shared/Theme/ThemeComponent';
import '@/components/main.scss';
import '@/components/modern-overrides.scss';
import '@asi-lda/pnet-auth-web/styles.css';
import { GlobalFiltersProvider } from '@/hooks/useGlobalFilters';
import { RoleSelectionHost } from '@/components/shared/RoleSelection/RoleSelectionHost';
import { RoleChangedBanner } from '@/components/shared/RoleSelection/RoleChangedBanner';
import { RoleSwitchOverlay } from '@/components/shared/RoleSelection/RoleSwitchOverlay';
import { RolePreviewBanner } from '@/components/shared/RolePreview/RolePreviewBanner';

// Shared login/SSO kit. The provider calls `Amplify.configure` exactly once
// for this config object (Cognito mode only); in mock mode Amplify is never
// configured and the login screen posts the gateway's `mock/login`.
// CheckEmail / LoginData / Logout go to the identity service when
// VITE_IDENTITY_API_URL is set (moved from the gateway); VITE_AUTH_SESSION=cookie
// keeps the refresh token in its httpOnly cookie (src/config/authSession.ts).
const authConfig: PnetAuthConfig = {
  amplify: amplifyConfig ?? undefined,
  tokenKind: authTokenKind,
  gatewayBaseUrl: import.meta.env.VITE_API_URL,
  ...(identityApiUrl ? { identityBaseUrl: identityApiUrl } : {}),
  session: authSessionMode,
  sessionClient: SESSION_CLIENT_KEY,
  authMode: isMockAuthEnabled ? 'mock' : 'cognito',
};

function App() {
  const isZoomedIn = useZoomDetection();

  useEffect(() => {
    if (import.meta.env.VITE_MUI_LICENSE_KEY) {
      LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD) {
      return undefined;
    }

    const preventContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };

    const preventRestrictedKeys = (event: KeyboardEvent) => {
      if (
        event.key === 'F12' ||
        (event.ctrlKey && event.shiftKey && event.key === 'I') ||
        (event.ctrlKey && event.shiftKey && event.key === 'J') ||
        (event.ctrlKey && event.key === 'U')
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('keydown', preventRestrictedKeys);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', preventRestrictedKeys);
    };
  }, []);

  return (
    <PnetAuthProvider config={authConfig}>
      <SessionRegistry />
      {/* Server data and the session live in the TanStack Query cache, in memory only
          (UI state plan phases 0 + 1); outside the router. */}
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <I18nextProvider i18n={i18n}>
            <StyledEngineProvider injectFirst>
              <ThemeComponentProvider>
                <ConfirmationProvider />
                <LoadingProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <SidebarNavigationProvider>
                      <GlobalFiltersProvider>
                        <div className={isZoomedIn ? 'App browserZoom' : 'App'}>
                          <ToastContainer stacked />
                          {/* Role selection screen + stale-tab banner (Authorization-Plan §13.6) */}
                          <RoleSelectionHost />
                          <RoleChangedBanner />
                          {/* Cover while this tab switches role, until the reload */}
                          <RoleSwitchOverlay />
                          {/* Role preview: read-only warning bar (Authorization-Plan §13.7) */}
                          <RolePreviewBanner />
                          <Pages />
                        </div>
                      </GlobalFiltersProvider>
                    </SidebarNavigationProvider>
                  </LocalizationProvider>
                </LoadingProvider>
              </ThemeComponentProvider>
            </StyledEngineProvider>
          </I18nextProvider>
        </SessionProvider>
      </QueryClientProvider>
    </PnetAuthProvider>
  );
}

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(<App />);
